import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  booleanAttribute,
} from '@angular/core';
import { Hotel } from 'app/shared/model';

export class FilterValue {
  selectedMultiple: string[] = [];
  values: string[] = [];
  selected: string;
}

@Component({
    selector: 'mh-core-lib-filter-hotels',
    templateUrl: './hotels-filter.component.html',
    styleUrls: ['./hotels-filter.component.scss'],
    standalone: false
})
export class CoreLibHotelsFilterComponent implements OnInit, OnDestroy, OnChanges {
  @Input()
  chainFilter: Hotel[] = [];

  @Input()
  disabled = false;

  @Input()
  multipleSelectionMode = true;

  /**
   * Siempre queda al menos un hotel marcado, como en el filtro legacy (`widget-filter-chain`):
   * el último no se puede desmarcar y "Deseleccionar todos" deja solo el primero.
   * Opt-in: las pantallas que lo usan no saben mostrar una selección vacía.
   */
  @Input({ transform: booleanAttribute })
  requireSelection = false;

  hotelsFilter: FilterValue = new FilterValue();
  isOpen = false;
  searchTerm = '';
  allSelected = false;
  filteredValues: string[] = [];

  @Output()
  hotelsChanged: EventEmitter<Hotel[]> = new EventEmitter();

  @Output()
  userSelectionChanged: EventEmitter<{ all: boolean }> = new EventEmitter();

  constructor() {}

  ngOnInit(): void {
    this.initializeHotels();
  }

  ngOnDestroy(): void {}

  ngOnChanges(changes: SimpleChanges): void {
    // Solo las opciones cambian la selección; `disabled` y los modos no
    if (changes['chainFilter']) {
      this.initializeHotels();
    }
  }

  initializeHotels(): void {
    if (this.chainFilter && this.chainFilter.length > 0) {
      this.hotelsFilter.values = this.chainFilter.map((hotel) => hotel.name).sort();
      this.filteredValues = [...this.hotelsFilter.values];

      this.hotelsFilter.selectedMultiple = this.hotelsFilter.selectedMultiple.filter((selectedHotel) =>
        this.hotelsFilter.values.includes(selectedHotel),
      );

      if (this.hotelsFilter.selectedMultiple.length === 0) {
        this.hotelsFilter.selectedMultiple = [...this.hotelsFilter.values];
      }
      this.updateAllSelectedState();
      // Se emite también cuando se conserva parte de la selección (p. ej. al cambiar de zona):
      // si no, la pantalla muestra la zona completa mientras el filtro muestra solo los marcados
      this.emitChanges();
    } else {
      this.hotelsFilter.values = [];
      this.hotelsFilter.selectedMultiple = [];
      this.filteredValues = [];
      this.allSelected = false;
    }
  }

  toggleDropdown(): void {
    if (!this.disabled) {
      this.isOpen = !this.isOpen;
    }
  }

  onSearchChange(event: any): void {
    const searchValue = event.target.value.toLowerCase();
    this.searchTerm = searchValue;

    this.filteredValues = this.hotelsFilter.values.filter((hotel) => hotel.toLowerCase().includes(searchValue));
  }

  isOptionSelected(hotelName: string): boolean {
    return this.hotelsFilter.selectedMultiple.includes(hotelName);
  }

  selectOption(hotelName: string): void {
    if (this.disabled) {
      return;
    }

    const index = this.hotelsFilter.selectedMultiple.indexOf(hotelName);

    if (index > -1 && this.isLastSelected(hotelName)) {
      return;
    }

    if (index > -1) {
      this.hotelsFilter.selectedMultiple.splice(index, 1);
    } else {
      this.hotelsFilter.selectedMultiple.push(hotelName);
    }

    this.updateAllSelectedState();
    this.emitChanges();
    this.userSelectionChanged.emit({ all: this.allSelected });
  }

  selectAll(): void {
    if (this.disabled) {
      return;
    }

    this.hotelsFilter.selectedMultiple = [...this.hotelsFilter.values];
    this.allSelected = true;
    this.emitChanges();
    this.userSelectionChanged.emit({ all: this.allSelected });
  }

  deselectAll(): void {
    if (this.disabled) {
      return;
    }

    this.hotelsFilter.selectedMultiple = this.requireSelection ? this.hotelsFilter.values.slice(0, 1) : [];
    this.updateAllSelectedState();
    this.emitChanges();
    this.userSelectionChanged.emit({ all: this.allSelected });
  }

  /** Con `requireSelection`, el único hotel marcado no se puede desmarcar. */
  isLastSelected(hotelName: string): boolean {
    return (
      this.requireSelection &&
      this.hotelsFilter.selectedMultiple.length === 1 &&
      this.hotelsFilter.selectedMultiple[0] === hotelName
    );
  }

  private updateAllSelectedState(): void {
    this.allSelected = this.hotelsFilter.selectedMultiple.length === this.hotelsFilter.values.length;
  }

  private emitChanges(): void {
    const selectedHotels = this.chainFilter.filter((hotel) => this.hotelsFilter.selectedMultiple.includes(hotel.name));
    this.hotelsChanged.emit(selectedHotels);
  }

  get selectedCount(): number {
    return this.hotelsFilter.selectedMultiple.length;
  }

  get totalCount(): number {
    return this.hotelsFilter.values.length;
  }

  get displayText(): string {
    if (this.selectedCount === 0) {
      return 'shared.filters.hotels.no_selection';
    }

    if (this.selectedCount === this.totalCount) {
      return 'shared.filters.hotels.all_selected';
    }

    if (this.selectedCount <= 2) {
      return this.hotelsFilter.selectedMultiple.join(', ');
    }

    return `shared.filters.hotels.selected_count`;
  }
}
